import { extractVariables } from './buildTemplatePayload';

const NAME_MAX = 512;
const BODY_MAX = 1024;
const HEADER_MAX = 60;
const FOOTER_MAX = 60;
const BUTTON_LABEL_MAX = 25;
const MAX_EMOJIS = 10;
const BUTTON_LIMITS = { total: 10, URL: 2, PHONE_NUMBER: 1, COPY_CODE: 1 };

const EMOJI_REGEX = /\p{Extended_Pictographic}/gu;
const SHORTENER_REGEX =
  /(^|[/.])(bit\.ly|tinyurl\.com|t\.co|goo\.gl|cutt\.ly|is\.gd|rebrand\.ly)(\/|$)/i;
const TEST_NAME_REGEX = /(^|_)(test|teste|sample)(_|$)/;
const OFFER_WORDS = [
  'oferta',
  'desconto',
  'promoção',
  'promocao',
  'grátis',
  'gratis',
  'cupom',
  'compre',
  'aproveite',
  'últimas vagas',
  'ultimas vagas',
  'imperdível',
  'imperdivel',
  '%',
];
export const OPT_OUT_BUTTON_TEXT = 'Não quero receber';
const OPT_OUT_REGEX = /n[aã]o quero receber|parar|sair|cancelar inscri/i;

const countEmojis = text => (text || '').match(EMOJI_REGEX)?.length || 0;
const normalize = text => (text || '').trim().toLowerCase();

const bodyVariableChecks = form => {
  const issues = [];
  const body = form.body || '';
  const keys = extractVariables(body);
  if (/^\s*\{\{\d+\}\}/.test(body)) {
    issues.push({ level: 'error', code: 'VAR_AT_START' });
  }
  if (/\{\{\d+\}\}[\s.,!?;:]*$/.test(body)) {
    issues.push({ level: 'error', code: 'VAR_AT_END' });
  }
  if (/\{\{\d+\}\}\s*\{\{\d+\}\}/.test(body)) {
    issues.push({ level: 'error', code: 'VAR_ADJACENT' });
  }
  if (keys.some((key, index) => Number(key) !== index + 1)) {
    issues.push({ level: 'error', code: 'VAR_NOT_SEQUENTIAL' });
  }
  keys
    .filter(key => !form.bodyExamples?.[key]?.trim())
    .forEach(variable =>
      issues.push({
        level: 'error',
        code: 'VAR_EXAMPLE_MISSING',
        params: { variable },
      })
    );
  const fixedWords = body
    .replace(/\{\{\d+\}\}/g, ' ')
    .split(/\s+/)
    .filter(Boolean).length;
  if (keys.length && fixedWords < 2 * keys.length + 1) {
    issues.push({ level: 'warn', code: 'TOO_MANY_VARS_FOR_TEXT' });
  }
  return issues;
};

const nameChecks = form => {
  const issues = [];
  if (!form.name || !/^[a-z0-9_]+$/.test(form.name)) {
    issues.push({ level: 'error', code: 'NAME_INVALID' });
  }
  if ((form.name || '').length > NAME_MAX) {
    issues.push({ level: 'error', code: 'NAME_TOO_LONG' });
  }
  if (TEST_NAME_REGEX.test(form.name || '')) {
    issues.push({ level: 'warn', code: 'NAME_LOOKS_TEST' });
  }
  return issues;
};

const bodyChecks = form => {
  const issues = [];
  const body = form.body || '';
  if (!body.trim()) issues.push({ level: 'error', code: 'BODY_EMPTY' });
  if (body.length > BODY_MAX) {
    issues.push({ level: 'error', code: 'BODY_TOO_LONG' });
  }
  if (/\t| {4}/.test(body)) {
    issues.push({ level: 'error', code: 'BODY_TAB_OR_SPACES' });
  }
  if (countEmojis(body) > MAX_EMOJIS) {
    issues.push({ level: 'warn', code: 'MANY_EMOJIS' });
  }
  return [...issues, ...bodyVariableChecks(form)];
};

const headerChecks = form => {
  const issues = [];
  if (form.headerFormat === 'TEXT') {
    const text = form.headerText || '';
    if (text.length > HEADER_MAX) {
      issues.push({ level: 'error', code: 'HEADER_TOO_LONG' });
    }
    if (countEmojis(text)) {
      issues.push({ level: 'error', code: 'HEADER_EMOJI' });
    }
    const keys = extractVariables(text);
    if (keys.length > 1) {
      issues.push({ level: 'error', code: 'HEADER_TOO_MANY_VARS' });
    }
    keys
      .filter(key => !form.headerExamples?.[key]?.trim())
      .forEach(variable =>
        issues.push({
          level: 'error',
          code: 'VAR_EXAMPLE_MISSING',
          params: { variable },
        })
      );
  } else if (
    ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerFormat) &&
    !form.headerHandle
  ) {
    issues.push({ level: 'error', code: 'HEADER_MEDIA_MISSING' });
  }
  return issues;
};

const footerChecks = form => {
  const issues = [];
  if ((form.footer || '').length > FOOTER_MAX) {
    issues.push({ level: 'error', code: 'FOOTER_TOO_LONG' });
  }
  if (extractVariables(form.footer).length) {
    issues.push({ level: 'error', code: 'FOOTER_HAS_VAR' });
  }
  return issues;
};

const buttonChecks = form => {
  const issues = [];
  const buttons = form.buttons || [];
  const count = type => buttons.filter(b => b.type === type).length;
  if (
    buttons.length > BUTTON_LIMITS.total ||
    count('URL') > BUTTON_LIMITS.URL ||
    count('PHONE_NUMBER') > BUTTON_LIMITS.PHONE_NUMBER ||
    count('COPY_CODE') > BUTTON_LIMITS.COPY_CODE
  ) {
    issues.push({ level: 'error', code: 'BUTTON_LIMITS' });
  }
  const seen = new Set();
  buttons.forEach(button => {
    if (button.type === 'COPY_CODE') {
      if (!button.example) {
        issues.push({ level: 'error', code: 'COPY_CODE_EXAMPLE_MISSING' });
      }
      return;
    }
    const text = button.text || '';
    if (text.length > BUTTON_LABEL_MAX) {
      issues.push({ level: 'error', code: 'BUTTON_LABEL_TOO_LONG' });
    }
    if (countEmojis(text)) {
      issues.push({ level: 'error', code: 'BUTTON_LABEL_EMOJI' });
    }
    if (button.type === 'QUICK_REPLY') {
      const key = normalize(text);
      if (key && seen.has(key)) {
        issues.push({ level: 'error', code: 'BUTTON_DUPLICATE' });
      }
      seen.add(key);
    }
    if (button.type === 'URL') {
      if (!button.url)
        issues.push({ level: 'error', code: 'BUTTON_URL_MISSING' });
      if (SHORTENER_REGEX.test(button.url || '')) {
        issues.push({ level: 'warn', code: 'URL_SHORTENER' });
      }
      if (extractVariables(button.url).length && !button.urlExample) {
        issues.push({
          level: 'error',
          code: 'VAR_EXAMPLE_MISSING',
          params: { variable: extractVariables(button.url)[0] },
        });
      }
    }
    if (button.type === 'PHONE_NUMBER' && !button.phoneNumber) {
      issues.push({ level: 'error', code: 'BUTTON_PHONE_MISSING' });
    }
  });
  return issues;
};

const categoryChecks = form => {
  const issues = [];
  const text = normalize(`${form.headerText || ''} ${form.body}`);
  if (
    form.category === 'UTILITY' &&
    OFFER_WORDS.some(word => text.includes(word))
  ) {
    issues.push({ level: 'warn', code: 'UTILITY_SOUNDS_MARKETING' });
  }
  if (form.category === 'MARKETING') {
    const hasOptOut = (form.buttons || []).some(
      b => b.type === 'QUICK_REPLY' && OPT_OUT_REGEX.test(b.text || '')
    );
    if (!hasOptOut) {
      issues.push({ level: 'warn', code: 'MARKETING_WITHOUT_OPT_OUT' });
    }
  }
  return issues;
};

export const checkTemplate = form => {
  if (form.category === 'AUTHENTICATION') return nameChecks(form);
  return [
    ...nameChecks(form),
    ...headerChecks(form),
    ...bodyChecks(form),
    ...footerChecks(form),
    ...buttonChecks(form),
    ...categoryChecks(form),
  ];
};
